import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useToast } from '../../contexts/ToastContext';
import { getMyNotifications } from '../../api/client';
import PageHeader from '../../components/layout/PageHeader';
import EmptyState from '../../components/layout/EmptyState';
import LoadingScreen from '../../components/layout/LoadingScreen';
import { Bell, Check, Clock, MessageSquare, CreditCard, Award, BookOpen } from 'lucide-react';

const iconMap: Record<string, React.ElementType> = {
  enrollment: BookOpen,
  payment: CreditCard,
  certificate: Award,
  announcement: MessageSquare,
  reminder: Clock,
  default: Bell,
};

const colorMap: Record<string, string> = {
  enrollment: 'bg-amber-50 text-amber-500',
  payment: 'bg-green-50 text-green-600',
  certificate: 'bg-purple-50 text-purple-600',
  announcement: 'bg-amber-50 text-amber-600',
  reminder: 'bg-slate-50 text-slate-600',
  default: 'bg-amber-50 text-amber-500',
};

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export default function NotificationsScreen(): React.JSX.Element {
  const toast = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMyNotifications();
        setNotifications(res.notifications || []);
      } catch (error: any) {
        toast.show(error.message || 'Failed to load notifications', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const markAsRead = (id: number) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  if (loading) return <LoadingScreen message="Loading notifications..." />;

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 animate-fade-in">
      <PageHeader
        title="Notifications"
        subtitle={`${unreadCount} unread`}
      />

      {notifications.length === 0 ? (
        <EmptyState
          icon={Bell}
          title="No notifications"
          description="You're all caught up! Check back later for updates."
        />
      ) : (
        <div className="space-y-3">
          {notifications.map((notification, idx) => {
            const Icon = iconMap[notification.type] || iconMap.default;
            const colorClass = colorMap[notification.type] || colorMap.default;
            return (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`flex items-start gap-4 p-4 rounded-2xl border transition-all ${
                  notification.is_read
                    ? 'bg-white border-slate-100'
                    : 'bg-amber-50/50 border-amber-100'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className={`text-sm font-semibold ${notification.is_read ? 'text-slate-700' : 'text-slate-900'}`}>
                        {notification.title}
                      </h4>
                      <p className="text-sm text-slate-500 mt-0.5">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="flex-shrink-0 p-1.5 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-amber-500 hover:border-amber-200 transition-colors"
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
